import React, { useEffect, useState, useCallback } from "react";
import MailingListForm from "./MailingListForm";
import { popupDismissedThisSession, markPopupDismissed, hasSubmittedThisSession } from "../../lib/mailingList";
import { trackEvent } from "../../lib/analytics";
import { isOtherPopupOpen } from "../../lib/popupGate";

/**
 * <MailingListPopup /> — timed mailing-list popup, shown once per session.
 *
 * Three triggers (all respect the per-session dismiss flag):
 *   1. Timed   — 30s on page + ≥25% scroll OR 30s + 15s grace (short pages)
 *   2. Exit-intent — desktop cursor leaves doc at top (after 5s grace)
 *   3. Back-button — sentinel pushState + popstate listener
 */

const DELAY_MS = 30 * 1000;
const SCROLL_THRESHOLD = 0.25;
const EXIT_INTENT_GRACE_MS = 5000;

export default function MailingListPopup() {
  const [open, setOpen] = useState(false);

  /* Stable dismiss callback — used by Esc / X / backdrop / form-success. */
  const dismiss = useCallback((e) => {
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    setOpen(false);
    markPopupDismissed();
    try {
      trackEvent("mailing_list_popup_dismissed", { metadata: { source: "timed_popup" } });
    } catch (_) { /* noop */ }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (popupDismissedThisSession() || hasSubmittedThisSession()) return undefined;

    let scrolledEnough = false;
    let timerFired = false;
    const mountedAt = Date.now();

    const consentBannerOpen = () => isOtherPopupOpen("mailing-list-popup");
    const showFor = (trigger) => {
      if (consentBannerOpen()) return false;
      if (popupDismissedThisSession() || hasSubmittedThisSession()) return false;
      setOpen(true);
      try { trackEvent("mailing_list_popup_shown", { metadata: { source: "timed_popup", trigger } }); } catch (_) {}
      return true;
    };

    /* Trigger 1: timer + scroll */
    const tryShowTimed = () => {
      if (!timerFired || !scrolledEnough) return;
      showFor("timed");
    };
    const onScroll = () => {
      const doc = document.documentElement;
      const ratio = (window.scrollY + window.innerHeight) / Math.max(doc.scrollHeight, 1);
      if (ratio >= SCROLL_THRESHOLD) {
        scrolledEnough = true;
        tryShowTimed();
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    const timer = window.setTimeout(() => {
      timerFired = true;
      window.setTimeout(() => { scrolledEnough = true; tryShowTimed(); }, 15000);
      tryShowTimed();
    }, DELAY_MS);

    /* Trigger 2: desktop exit-intent */
    const onMouseOut = (e) => {
      if (Date.now() - mountedAt < EXIT_INTENT_GRACE_MS) return;
      if (e.clientY > 0 || e.relatedTarget) return;
      showFor("exit_intent");
    };
    document.addEventListener("mouseout", onMouseOut);

    /* Trigger 3: browser back button */
    let pushedSentinel = false;
    try {
      window.history.pushState({ btkPopup: "sentinel" }, "");
      pushedSentinel = true;
    } catch (_) { /* noop */ }
    const onPopState = () => {
      if (Date.now() - mountedAt < 500) return;
      if (popupDismissedThisSession() || hasSubmittedThisSession()) return;
      const shown = showFor("back_button");
      if (shown) {
        try { window.history.pushState({ btkPopup: "sentinel" }, ""); } catch (_) {}
      }
    };
    window.addEventListener("popstate", onPopState);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.clearTimeout(timer);
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("popstate", onPopState);
      if (pushedSentinel) {
        try {
          if (window.history.state && window.history.state.btkPopup === "sentinel") {
            window.history.back();
          }
        } catch (_) {}
      }
    };
  }, []);

  /* Esc to close */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") dismiss(e); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, dismiss]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="mailing-popup-title"
      data-testid="mailing-list-popup"
      className="fixed inset-0 z-[80] flex items-center justify-center px-4 sm:px-6 py-10"
    >
      {/* Backdrop — plain div (not a <button>) so it doesn't fight pointer events at edges */}
      <div
        aria-hidden="true"
        onClick={dismiss}
        data-testid="mailing-list-popup-backdrop"
        className="absolute inset-0 w-full h-full bg-black/70 backdrop-blur-sm cursor-pointer"
        style={{ zIndex: 0 }}
      />

      {/* Card — relative + explicit higher z-index so backdrop never intercepts */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg rounded-2xl border border-gold/30 backdrop-blur-xl p-6 md:p-8 max-h-[90vh] overflow-y-auto"
        style={{
          zIndex: 1,
          background:
            "linear-gradient(135deg, rgba(8,8,13,0.98) 0%, rgba(4,6,12,0.98) 100%)",
          boxShadow: "0 30px 70px rgba(0,0,0,0.6), 0 0 36px rgba(212,175,55,0.18)",
        }}
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close"
          data-testid="mailing-list-popup-close"
          className="absolute top-3 right-3 w-10 h-10 rounded-full border border-white/15 hover:border-white/55 text-white/75 hover:text-white transition-colors flex items-center justify-center cursor-pointer"
          style={{ zIndex: 2 }}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" pointerEvents="none">
            <path d="M6 6l12 12M6 18L18 6" />
          </svg>
        </button>

        <div className="mb-5 pr-12">
          <span className="text-[11px] uppercase tracking-[0.28em] text-gold/85">Stay In The Loop</span>
          <h2
            id="mailing-popup-title"
            className="mt-2 font-display text-2xl md:text-3xl tracking-tight leading-tight"
          >
            Get insights, case studies & offers — straight to your inbox.
          </h2>
          <p className="mt-2 text-sm text-white/65 leading-relaxed">
            Join the Bow Tie Kreative list. No spam, just the good stuff. Unsubscribe anytime.
          </p>
        </div>

        <MailingListForm
          source="timed_popup"
          variant="popup"
          testidPrefix="mailing-list-popup-form"
          onSuccess={() => setTimeout(() => setOpen(false), 2200)}
        />
      </div>
    </div>
  );
}
